import { makeStyles, Slider } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";

export default function Rapid3Assessment({
  pid,
  token,
  appointmentId,
  currentRapid3Info,
  onRapid3InfoUpdate,
  onError,
}) {
  // Styles
  const useStyles = makeStyles({
    root: { height: 1, marginTop: 0 },
    thumb: {
      height: 24,
      width: 24,
      backgroundColor: "var(--darkbluecolorone)",
      marginTop: -10,
      marginLeft: -12,
    },
    track: {
      height: 5,
      borderRadius: 5,
      backgroundColor: "var(--darkbluecolorone)",
    },
    rail: {
      height: 5,
      borderRadius: 5,
    },
    valueLabel: {
      left: "calc(-50% + 8px)",
      top: -30,
      color: "var(--darkbluecolorone)",
    },
  });
  const sliderClasses = useStyles();

  const questions = [
    {
      key: "dress",
      text: "Dress yourself, including shoelaces and buttons?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "outOfBed",
      text: "Get in and out of bed?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "cupToMouth",
      text: "Lift a full cup or glass to your mouth?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "walkOutdoor",
      text: "Walk outdoors on flat ground?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "washBody",
      text: "Wash and dry your body?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "bendToPick",
      text: "Bend down to pick up clothing from the floor?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "turnFaucet",
      text: "Turn faucets on and off?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "inOutOfACar",
      text: "Get in and out of a car?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "walkTwoMiles",
      text: "Walk two miles or three kilometers, if you wish?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
    {
      key: "sportActivity",
      text: "Participate in recreational activities and sports as you would like, if you wish?",
      choices: [
        { label: "Without any difficulty", value: 1 },
        { label: "With some difficulty", value: 2 },
        { label: "With much difficulty", value: 3 },
        { label: "Unable to do", value: 4 },
      ],
    },
  ];

  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  const [physicalAbilities, setPhysicalAbilities] = useState({});
  const [pain, setPain] = useState(0);
  const [overallHealth, setOverallHealth] = useState(0);
  const [score, setScore] = useState(0);

  // Fetch current Rapid3 score and details (only if not already available)
  const fetchRapid3Score = useCallback(async () => {
    if (!pid || !token) return;

    try {
      const response = await fetch(`/api/v1/pain-score/rapid3/${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        responseData.current && onRapid3InfoUpdate(responseData.current);
      }
    } catch (error) {
      onError(error);
    } finally {
    }
  }, [pid, token, onRapid3InfoUpdate, onError]);

  const handlePhysicalChange = (questionKey, value) => {
    setPhysicalAbilities((prev) => ({
      ...prev,
      [questionKey]: Number(value),
    }));
  };

  const calculateScore = useCallback(async () => {
    if (!appointmentId || !token) return;

    setIsCalculating(true);

    const requestData = {
      ...physicalAbilities,
      pain: Number(pain),
      overallHealth: Number(overallHealth),
    };
    try {
      const res = await fetch(`/api/v1/pain-score/rapid3/${appointmentId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(requestData),
      });
      const data = await res.json();
      if (data?.status?.success) {
        await fetchRapid3Score();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsCalculating(false);
    }
  }, [
    appointmentId,
    token,
    physicalAbilities,
    pain,
    overallHealth,
    onRapid3InfoUpdate,
  ]);

  useEffect(() => {
    if (currentRapid3Info?.value && currentRapid3Info?.scoreDetail) {
      setScore(currentRapid3Info.value);
      setPain(currentRapid3Info.scoreDetail.pain || 0);
      setOverallHealth(currentRapid3Info.scoreDetail.overallHealth || 0);
      setPhysicalAbilities({
        dress: currentRapid3Info?.scoreDetail?.dress || 1,
        outOfBed: currentRapid3Info?.scoreDetail?.outOfBed || 1,
        cupToMouth: currentRapid3Info?.scoreDetail?.cupToMouth || 1,
        walkOutdoor: currentRapid3Info?.scoreDetail?.walkOutdoor || 1,
        washBody: currentRapid3Info?.scoreDetail?.washBody || 1,
        bendToPick: currentRapid3Info?.scoreDetail?.bendToPick || 1,
        turnFaucet: currentRapid3Info?.scoreDetail?.turnFaucet || 1,
        inOutOfACar: currentRapid3Info?.scoreDetail?.inOutOfACar || 1,
        walkTwoMiles: currentRapid3Info?.scoreDetail?.walkTwoMiles || 1,
        sportActivity: currentRapid3Info?.scoreDetail?.sportActivity || 1,
      });
    }
  }, [currentRapid3Info]);

  useEffect(() => {
    if (currentRapid3Info?.value && currentRapid3Info?.scoreDetail) return;

    const fetchData = async () => {
      setIsInitialLoading(true);

      await fetchRapid3Score();
      setIsInitialLoading(false);
    };
    fetchData();
  }, []);

  return (
    <>
      <div
        style={{
          background: "#E8ECEF",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "0% 0%",
          backgroundClip: "padding-box",
          padding: "20px 20px",
        }}
      >
        <div
          className=""
          style={{
            fontWeight: 600,
            fontSize: "16px",

            color: "#0060A6",
          }}
        >
          Physical Abilities
        </div>
        <div
          style={{
            fontWeight: 400,
            fontSize: "14px",

            color: "#0060A6",
          }}
        >
          Please make a selection for all questions.
        </div>
      </div>
      <div className="container mt-3">
        <div className="m-2 fw-bold">Are you able to?</div>
        {questions.map((q, index) => (
          <div className="mb-3 border-bottom rounded p-2" key={q.key}>
            <span className="d-block mb-2">
              {index + 1}. {q.text}
            </span>
            <div className="d-flex flex-column">
              {q.choices.map((choice, i) => (
                <div className="form-check mb-2" key={i}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`ability_${q.key}`}
                    value={choice.value}
                    checked={physicalAbilities[q.key] === choice.value}
                    onChange={(e) =>
                      handlePhysicalChange(q.key, Number(e.target.value))
                    }
                  />
                  <label className="form-check-label ms-2">
                    {choice.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="mb-3 border-bottom">
          <label className="form-label fw-bold">Pain Assessment (0-10)</label>
          <div className="mb-3">
            How much pain have you had because of your condition over the past
            week? Please indicate below how severe your pain has been:
          </div>
          <div className="d-flex justify-content-between">
            <span>0</span>
            <span>10</span>
          </div>
          <Slider
            classes={sliderClasses}
            value={pain}
            onChange={(_, newValue) => setPain(Number(newValue))}
            step={1}
            min={0}
            max={10}
            valueLabelDisplay="auto"
          />
          <div className="d-flex align-items-end justify-content-between mt-2 fs-6 text-muted  lh-1 mb-2">
            <span>No pain</span>
            <span className="text-end ">
              Pain as bad as <br />
              it could be
            </span>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Overall Health (0-10)</label>
          <div className="mb-3">
            Considering all the ways in which illness and health conditions may
            affect you at this time, please indicate below how you are doing:
          </div>
          <div className="d-flex justify-content-between">
            <span>0</span>
            <span>10</span>
          </div>
          <Slider
            classes={sliderClasses}
            value={overallHealth}
            onChange={(_, newValue) => setOverallHealth(Number(newValue))}
            step={1}
            min={0}
            max={10}
            valueLabelDisplay="auto"
          />
          <div className="d-flex align-items-end justify-content-between mt-2 text-muted fs-6 lh-1">
            <span>Very Well</span>
            <span className="text-end font-small">Very Poorly</span>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-4 btn_block w-100">
          <button
            className="btn btn_primary m-auto"
            disabled={isCalculating}
            onClick={calculateScore}
          >
            {isCalculating ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>
      <div
        className="mt-4 p-3 border rounded text-center d-flex align-items-center justify-content-between"
        style={{ backgroundColor: "#0060A6", color: "#FFFFFF" }}
      >
        <div className="fw-bold fs-4">RAPID3 Score</div>
        <div className="fs-4 fw-bold">{score}</div>
        <div>{currentRapid3Info?.description}</div>
      </div>
    </>
  );
}
